import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download, Ellipsis, Eye, Mail, Pencil, Plus, RefreshCw } from 'lucide-react';
import {
  ErrorState,
  KpiGrid,
  KpiTile,
  PageHeader,
  PersonAvatar,
  PersonCell,
  SearchField,
  SegmentedControl,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
  useDelayedFlag,
} from '../components/ui';
import type { StatusTone } from '../components/ui';
import { Employee, useGetEmployeesQuery } from '../store/slices/employeeSlice';
import { useSyncManagerLinksMutation } from '../store/slices/adminSlice';
import { useGetFilteredTimeOffEntriesQuery } from '../store/slices/timeOffSlice';
import { addNotification, openModal } from '../store/slices/uiSlice';
import { downloadFloorplanEmployees } from '../services/employeeExport';
import { sbg, transition, typeStyle } from '../theme/sbgTokens';
import { MISSING, formatCount, formatDate, formatNumber, formatTime } from '../utils/format';
import {
  AllocationPace,
  PaceStatus,
  TimeOffUsageSummary,
  buildEmployeeUsageMap,
  calculateAllocationPace,
  combineAllocationPaces,
  getEmployeeElapsedFraction,
  getYearProgress,
} from '../utils/timeOffUsage';
import { useEasternToday } from '../hooks/useEasternToday';
import { ATTENTION_STATUSES, PACE_META, PaceCell, PaceLegend, PaceStatusCell, worstPace } from './employees-parts/pace';

/* ------------------------------------------------------------------------ */
/* Model                                                                     */
/* ------------------------------------------------------------------------ */

const EMPTY_USAGE: TimeOffUsageSummary = {
  pto: { used: 0, scheduled: 0, pending: 0 },
  sick: { used: 0, scheduled: 0, pending: 0 },
  wfh: { used: 0, scheduled: 0, pending: 0 },
  combined: { used: 0, scheduled: 0, pending: 0 },
};

/** Former statuses are facts, not alerts: all neutral, the word carries the meaning. */
const EMPLOYMENT_STATUS: Record<Employee['employmentStatus'], { label: string; tone: StatusTone }> = {
  active: { label: 'Active', tone: 'success' },
  terminated: { label: 'Terminated', tone: 'neutral' },
  resigned_regrettable: { label: 'Resigned (regrettable)', tone: 'neutral' },
  resigned_unregrettable: { label: 'Resigned (unregrettable)', tone: 'neutral' },
};

const employmentStatusOf = (employee: Employee) =>
  EMPLOYMENT_STATUS[employee.employmentStatus] ?? { label: employee.employmentStatus || 'Active', tone: 'neutral' as StatusTone };

type StatusView = 'active' | 'former';
type PaceFilter = '' | 'attention' | PaceStatus;
type SortKey = 'name' | 'role' | 'department' | 'manager' | 'startDate' | 'lastDay' | 'pto' | 'sick' | 'pace';
type SortDirection = 'asc' | 'desc';

/** Filter sentinels. `''` = no filter. */
const ALL = '';
const NO_DEPARTMENT = '__none__';
const NUMERIC_SORTS: SortKey[] = ['pto', 'sick', 'pace'];

interface Column {
  key: string;
  label: string;
  sortKey?: SortKey;
  align?: 'right';
  minWidth?: number;
  sticky?: boolean;
  render: (row: EmployeeRow) => React.ReactNode;
}

interface EmployeeRow {
  employee: Employee;
  isActive: boolean;
  pto: AllocationPace;
  sick: AllocationPace;
  combined: AllocationPace;
  /** The most severe of the PTO, sick and combined statuses. */
  pace: PaceStatus;
}

const paceDetail = (row: EmployeeRow) =>
  `PTO: ${PACE_META[row.pto.status].label.toLowerCase()} · Sick: ${PACE_META[row.sick.status].label.toLowerCase()}`;

const sortValue = (row: EmployeeRow, key: SortKey): string | number | null => {
  const { employee } = row;
  switch (key) {
    case 'name': return employee.name || null;
    case 'role': return employee.role || null;
    case 'department': return employee.department?.name || null;
    case 'manager': return employee.managerName || null;
    case 'startDate': return employee.startDate || null;
    case 'lastDay': return employee.terminationDate || null;
    case 'pto': return row.pto.committed;
    case 'sick': return row.sick.committed;
    case 'pace': return PACE_META[row.pace].severity;
    default: return null;
  }
};

/** Missing values sort last in either direction; ties fall back to name. */
const compareRows = (key: SortKey, direction: SortDirection) => (a: EmployeeRow, b: EmployeeRow) => {
  const left = sortValue(a, key);
  const right = sortValue(b, key);
  if (left === null && right === null) return a.employee.name.localeCompare(b.employee.name);
  if (left === null) return 1;
  if (right === null) return -1;
  const result = typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), 'en', { sensitivity: 'base' });
  return (direction === 'asc' ? result : -result) || a.employee.name.localeCompare(b.employee.name);
};

/** Filter key for free-text values: trimmed, case-insensitive ("Employee" = "employee"). */
const filterKey = (value?: string | null) => (value || '').trim().toLowerCase();

/** "employee" → "Employee". Only the first letter changes, so "AE" stays "AE". */
const sentenceCase = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const capitals = (value: string) => value.replace(/[^A-Z]/g, '').length;

/**
 * One option per case-insensitive value. The label is the most common
 * spelling on file (ties go to the more capitalized one), formatted, so
 * "employee" and "Employee" are one option.
 */
const groupedOptions = (values: (string | null | undefined)[], format: (label: string) => string = (label) => label) => {
  const spellings = new Map<string, Map<string, number>>();
  values.forEach((raw) => {
    const spelling = (raw || '').trim();
    if (!spelling) return;
    const key = spelling.toLowerCase();
    const counts = spellings.get(key) ?? new Map<string, number>();
    counts.set(spelling, (counts.get(spelling) ?? 0) + 1);
    spellings.set(key, counts);
  });
  return Array.from(spellings, ([key, counts]) => {
    const [label] = Array.from(counts).sort((a, b) => b[1] - a[1] || capitals(b[0]) - capitals(a[0]) || a[0].localeCompare(b[0]))[0];
    return { value: key, label: format(label) };
  }).sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
};

/** Department scope shared by the header, the KPI tiles and the table. */
const inDepartment = (employee: Employee, departmentFilter: string) => {
  if (departmentFilter === ALL) return true;
  if (departmentFilter === NO_DEPARTMENT) return !filterKey(employee.department?.name);
  return filterKey(employee.department?.name) === departmentFilter;
};

/* ------------------------------------------------------------------------ */
/* Small presentational pieces                                               */
/* ------------------------------------------------------------------------ */

/** A KPI count with its unit word in quieter body type: "3 employees". */
const KpiCount: React.FC<{ value: number | null; unit: string; unitSingular: string }> = ({ value, unit, unitSingular }) =>
  value === null ? (
    <>{MISSING}</>
  ) : (
    <>
      {formatNumber(value)}
      <Box component="span" sx={{ ...typeStyle('body-md'), color: sbg.text.secondary, ml: 0.75 }}>
        {value === 1 ? unitSingular : unit}
      </Box>
    </>
  );

/** A compact toolbar select whose value reads as its label: "Department: Sales". */
const FilterSelect: React.FC<{
  label: string;
  allLabel: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ label, allLabel, value, options, onChange, disabled }) => (
  <TextField
    select
    size="small"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
    slotProps={{
      htmlInput: { 'aria-label': label },
      select: {
        displayEmpty: true,
        renderValue: (selected) => {
          if (selected === ALL) return allLabel;
          const option = options.find((item) => item.value === selected);
          return `${label}: ${option?.label ?? String(selected)}`;
        },
        MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } },
      },
    }}
    sx={{ flex: { xs: '1 1 150px', sm: '0 0 auto' }, minWidth: { sm: 168 }, maxWidth: { sm: 260 } }}
  >
    <MenuItem value={ALL}>{allLabel}</MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
);

/**
 * One line of text that ellipsizes, with the full value in a tooltip — but
 * only when it is actually cut off.
 */
const TruncatedText: React.FC<{
  text: string;
  variant?: 'body1' | 'body2' | 'labelMd';
  color?: string;
}> = ({ text, variant = 'body1', color = sbg.text.primary }) => {
  const [clipped, setClipped] = useState(false);
  return (
    <Tooltip title={clipped ? text : ''} placement="top-start">
      <Typography
        variant={variant}
        component="span"
        noWrap
        onMouseEnter={(event) => setClipped(event.currentTarget.scrollWidth > event.currentTarget.clientWidth)}
        sx={{ display: 'block', color }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

/** Two-line text cell: primary value, optional quieter second line. */
const TextCell: React.FC<{ primary?: string | null; secondary?: string | null }> = ({ primary, secondary }) => (
  <Box sx={{ minWidth: 0, maxWidth: 200 }}>
    {primary ? (
      <TruncatedText text={primary} />
    ) : (
      <Typography variant="body1" component="span" sx={{ display: 'block', color: sbg.text.secondary }}>
        {MISSING}
      </Typography>
    )}
    {secondary && <TruncatedText text={secondary} variant="body2" color={sbg.text.secondary} />}
  </Box>
);

/** A manager in a row: small avatar and a regular-weight name, so the Employee column leads. */
const ManagerCell: React.FC<{ name: string }> = ({ name }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: 176 }}>
    <PersonAvatar name={name} size={24} />
    <Box sx={{ minWidth: 0 }}>
      <TruncatedText text={name} />
    </Box>
  </Box>
);

/** A calendar date ("Mar 4, 2024"), or an em dash. */
const DateText: React.FC<{ value?: string | null }> = ({ value }) => (
  <Typography variant="body1" component="span" sx={{ whiteSpace: 'nowrap', color: value ? sbg.text.primary : sbg.text.secondary }}>
    {formatDate(value)}
  </Typography>
);

const STICKY_CLASS = 'employees-sticky-cell';

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusView, setStatusView] = useState<StatusView>('active');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);
  const [roleFilter, setRoleFilter] = useState<string>(ALL);
  const [paceFilter, setPaceFilter] = useState<PaceFilter>(ALL);
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'name', direction: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data: employees = [], isLoading, error, refetch, fulfilledTimeStamp } = useGetEmployeesQuery();
  // Usage only aggregates entries STARTING in the current year — bound
  // start_date on both sides so future-year bookings don't ride along just to
  // be discarded client-side.
  const todayYmd = useEasternToday();
  const yearProgress = useMemo(() => getYearProgress(todayYmd), [todayYmd]);
  const currentYear = yearProgress.year;
  const {
    data: timeOffEntries = [],
    isLoading: isLoadingUsage,
    isError: hasUsageError,
    refetch: refetchUsage,
  } = useGetFilteredTimeOffEntriesQuery({
    startDate: `${currentYear}-01-01`,
    startBefore: `${currentYear}-12-31`,
  });
  const [syncManagerLinks, { isLoading: isSyncing }] = useSyncManagerLinksMutation();
  const showUsageSkeleton = useDelayedFlag(isLoadingUsage);

  const usageByEmployee = useMemo(
    () => buildEmployeeUsageMap(timeOffEntries, currentYear, todayYmd),
    [timeOffEntries, currentYear, todayYmd]
  );

  const rows = useMemo<EmployeeRow[]>(
    () =>
      employees.map((employee) => {
        const usage = usageByEmployee.get(employee._id) || EMPTY_USAGE;
        const elapsedFraction = getEmployeeElapsedFraction(
          currentYear,
          todayYmd,
          employee.customAllotment ? undefined : employee.startDate
        );
        const pto = calculateAllocationPace(usage.pto, employee.ptoAllotment, elapsedFraction);
        const sick = calculateAllocationPace(usage.sick, employee.sickTimeAllotment, elapsedFraction);
        const combined = combineAllocationPaces(pto, sick);
        return {
          employee,
          isActive: employee.employmentStatus === 'active' || !employee.employmentStatus,
          pto,
          sick,
          combined,
          pace: worstPace(pto.status, sick.status, combined.status),
        };
      }),
    [employees, usageByEmployee, currentYear, todayYmd]
  );

  const activeRows = useMemo(() => rows.filter((row) => row.isActive), [rows]);
  const formerRows = useMemo(() => rows.filter((row) => !row.isActive), [rows]);
  const baseRows = statusView === 'former' ? formerRows : activeRows;
  const usageReady = !isLoadingUsage && !hasUsageError;

  const departmentOptions = useMemo(() => {
    const options = groupedOptions(employees.map((employee) => employee.department?.name));
    if (employees.some((employee) => !filterKey(employee.department?.name))) {
      options.push({ value: NO_DEPARTMENT, label: 'No department' });
    }
    return options;
  }, [employees]);
  const roleOptions = useMemo(() => groupedOptions(employees.map((employee) => employee.role), sentenceCase), [employees]);
  const departmentLabel = departmentFilter === ALL
    ? 'All departments'
    : departmentOptions.find((option) => option.value === departmentFilter)?.label ?? departmentFilter;
  const roleLabel = roleOptions.find((option) => option.value === roleFilter)?.label ?? roleFilter;
  // Rows show each role the way the Role filter names it, so one role reads one way.
  const roleLabels = useMemo(() => new Map(roleOptions.map((option) => [option.value, option.label])), [roleOptions]);

  // The header names the department scope, so the KPI tiles count within it:
  // a tile's number matches what selecting it shows in the table.
  const scopedActiveRows = useMemo(
    () => activeRows.filter((row) => inDepartment(row.employee, departmentFilter)),
    [activeRows, departmentFilter]
  );
  const scopedFormerCount = useMemo(
    () => formerRows.filter((row) => inDepartment(row.employee, departmentFilter)).length,
    [formerRows, departmentFilter]
  );
  const paceOptions = useMemo(
    () => [
      { value: 'attention', label: 'Needs attention' },
      ...(Object.keys(PACE_META) as PaceStatus[]).map((status) => ({ value: status, label: PACE_META[status].label })),
    ],
    []
  );

  const paceCounts = useMemo(() => {
    const counts: Record<PaceStatus, number> = {
      'over-allotment': 0,
      'over-pace': 0,
      'pending-risk': 0,
      'within-pace': 0,
      'no-allotment': 0,
    };
    scopedActiveRows.forEach((row) => { counts[row.pace] += 1; });
    return counts;
  }, [scopedActiveRows]);

  const query = searchTerm.trim().toLowerCase();
  const filteredRows = useMemo(
    () =>
      baseRows.filter(({ employee, pace }) => {
        if (
          query &&
          !(
            employee.name.toLowerCase().includes(query) ||
            employee.email.toLowerCase().includes(query) ||
            employee.role.toLowerCase().includes(query) ||
            (employee.teamName || '').toLowerCase().includes(query) ||
            (employee.managerName || '').toLowerCase().includes(query) ||
            (employee.department?.name || '').toLowerCase().includes(query)
          )
        ) {
          return false;
        }
        if (!inDepartment(employee, departmentFilter)) return false;
        if (roleFilter && filterKey(employee.role) !== roleFilter) return false;
        if (paceFilter && statusView === 'active' && usageReady) {
          if (paceFilter === 'attention' ? !ATTENTION_STATUSES.includes(pace) : pace !== paceFilter) return false;
        }
        return true;
      }),
    [baseRows, query, departmentFilter, roleFilter, paceFilter, statusView, usageReady]
  );

  const sortedRows = useMemo(
    () => [...filteredRows].sort(compareRows(sort.key, sort.direction)),
    [filteredRows, sort]
  );

  const maxPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, maxPage);
  const pageRows = sortedRows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const paceFilterActive = Boolean(paceFilter) && statusView === 'active';
  const hasFilters = Boolean(query || departmentFilter || roleFilter || paceFilterActive);

  /* ---- handlers ------------------------------------------------------- */

  const resetPage = () => setPage(0);

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter(ALL);
    setRoleFilter(ALL);
    setPaceFilter(ALL);
    resetPage();
  };

  const handleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: NUMERIC_SORTS.includes(key) ? 'desc' : 'asc' }
    );
    resetPage();
  };

  const handlePaceShortcut = (status: PaceStatus) => {
    setStatusView('active');
    setPaceFilter((current) => (current === status ? ALL : status));
    resetPage();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, employee: Employee) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleAddEmployee = () => {
    dispatch(
      openModal({
        type: 'employee-form',
        data: { isEdit: false },
      })
    );
  };

  const handleSyncManagers = async () => {
    if (isSyncing) return;
    setSyncError(null);
    try {
      const result = await syncManagerLinks().unwrap();
      dispatch(addNotification({
        type: 'success',
        message:
          `Manager links synced. Updated ${formatCount(result.employeeManagerUpdates, 'employee manager')}, ` +
          `${formatCount(result.departmentManagerUpdates, 'department manager')} and ` +
          `${formatCount(result.employeeUserUpdates, 'employee sign-in link')}.`,
      }));
    } catch (err: any) {
      const reason = String(err?.data?.error || err?.message || '').trim().replace(/\.$/, '');
      setSyncError(`Couldn’t sync manager links. ${reason || 'The server didn’t respond'}. Try again in a minute.`);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadFloorplanEmployees();
    } catch {
      dispatch(addNotification({
        type: 'error',
        message: 'Couldn’t export employees. The server didn’t respond. Try again in a minute.',
      }));
    } finally {
      setIsExporting(false);
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    handleMenuClose();
    dispatch(
      openModal({
        type: 'employee-form',
        data: { employee, isEdit: true },
      })
    );
  };

  const handleViewEmployee = (employee: Employee) => {
    navigate(`/employees/${employee._id}`);
  };

  /* ---- columns -------------------------------------------------------- */

  const usageState = { loading: isLoadingUsage, showSkeleton: showUsageSkeleton, error: hasUsageError };

  const columns: Column[] = [
    {
      key: 'name',
      label: 'Employee',
      sortKey: 'name',
      minWidth: 200,
      sticky: true,
      render: ({ employee }) => (
        <PersonCell
          name={<TruncatedText text={employee.name} variant="labelMd" />}
          avatarName={employee.name}
          secondary={employee.email ? <TruncatedText text={employee.email} variant="body2" color={sbg.text.secondary} /> : null}
          sx={{ maxWidth: { xs: 200, sm: 248 } }}
        />
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortKey: 'role',
      minWidth: 120,
      // The team line only when it adds something the Department column doesn't say.
      render: ({ employee }) => (
        <TextCell
          primary={employee.role ? roleLabels.get(filterKey(employee.role)) ?? sentenceCase(employee.role.trim()) : null}
          secondary={employee.teamName && filterKey(employee.teamName) !== filterKey(employee.department?.name) ? employee.teamName : null}
        />
      ),
    },
    {
      key: 'department',
      label: 'Department',
      sortKey: 'department',
      minWidth: 104,
      render: ({ employee }) => <TextCell primary={employee.department?.name} />,
    },
    {
      key: 'manager',
      label: 'Manager',
      sortKey: 'manager',
      minWidth: 132,
      render: ({ employee }) => (employee.managerName ? <ManagerCell name={employee.managerName} /> : <TextCell primary={null} />),
    },
    {
      key: 'startDate',
      label: 'Start date',
      sortKey: 'startDate',
      minWidth: 100,
      render: ({ employee }) => <DateText value={employee.startDate} />,
    },
    ...(statusView === 'former'
      ? [
          {
            key: 'lastDay',
            label: 'Last day',
            sortKey: 'lastDay' as SortKey,
            minWidth: 150,
            render: ({ employee }: EmployeeRow) => {
              const status = employmentStatusOf(employee);
              return (
                <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'start' }}>
                  <DateText value={employee.terminationDate} />
                  <StatusPill tone={status.tone}>{status.label}</StatusPill>
                </Box>
              );
            },
          },
        ]
      : []),
    {
      key: 'pto',
      label: 'PTO',
      sortKey: 'pto',
      align: 'right',
      minWidth: 116,
      render: (row) => <PaceCell kind="PTO" pace={row.pto} showPace={row.isActive} {...usageState} />,
    },
    {
      key: 'sick',
      label: 'Sick',
      sortKey: 'sick',
      align: 'right',
      minWidth: 116,
      render: (row) => <PaceCell kind="Sick" pace={row.sick} showPace={row.isActive} {...usageState} />,
    },
    ...(statusView === 'active'
      ? [
          {
            key: 'pace',
            label: 'Pace',
            sortKey: 'pace' as SortKey,
            minWidth: 136,
            render: (row: EmployeeRow) => (
              <PaceStatusCell status={row.pace} total={row.combined} detail={paceDetail(row)} {...usageState} />
            ),
          },
        ]
      : []),
  ];
  const columnCount = columns.length + 1;

  // Pinned from `sm` up. On a phone a pinned name column would cover most of
  // the scroll area, so it scrolls with the rest of the row.
  const stickyPosition = { xs: 'static', sm: 'sticky' } as const;
  const stickyHeadSx = { position: stickyPosition, left: 0, zIndex: 3, bgcolor: sbg.bg.surfaceSubtle } as const;
  const stickyBodySx = { position: stickyPosition, left: 0, zIndex: 1, bgcolor: sbg.bg.surface, transition: transition('background-color') } as const;

  /* ---- regions -------------------------------------------------------- */

  const activeFilterSummary = [
    query && `“${searchTerm.trim()}”`,
    departmentFilter && departmentLabel,
    roleFilter && roleLabel,
    paceFilterActive && paceOptions.find((option) => option.value === paceFilter)?.label,
  ].filter(Boolean).join(' · ');

  const viewNoun = statusView === 'former' ? 'former employee' : 'active employee';
  // The unfiltered total is already in the segmented control and Headcount;
  // the toolbar only speaks up when filters narrow the list.
  const countLabel = isLoading || error || !hasFilters
    ? ''
    : `${formatNumber(filteredRows.length)} of ${formatCount(baseRows.length, viewNoun)}`;

  let tableBody: React.ReactNode;
  if (isLoading) {
    tableBody = <TableSkeletonRows columns={columnCount} rows={6} />;
  } else if (error) {
    tableBody = (
      <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
        <TableCell colSpan={columnCount} sx={{ borderBottom: 0 }}>
          <ErrorState
            compact
            title="Couldn’t load employees"
            body="The server didn’t respond. Try again in a minute; your filters are kept."
            onRetry={() => { refetch(); }}
          />
        </TableCell>
      </TableRow>
    );
  } else if (employees.length === 0) {
    tableBody = (
      <TableEmptyRow
        colSpan={columnCount}
        title="No employees yet"
        body="Employees appear here once you add them or import a CSV."
        action={
          <Button variant="outlined" startIcon={<Plus size={16} />} onClick={handleAddEmployee}>
            Add employee
          </Button>
        }
      />
    );
  } else if (baseRows.length === 0) {
    tableBody = statusView === 'former' ? (
      <TableEmptyRow
        colSpan={columnCount}
        title="No former employees"
        body="People marked as terminated or resigned appear here, with their last day."
        action={<Button variant="outlined" onClick={() => { setStatusView('active'); resetPage(); }}>Show active employees</Button>}
      />
    ) : (
      <TableEmptyRow
        colSpan={columnCount}
        title="No active employees"
        body="Everyone on file is marked as a former employee."
        action={<Button variant="outlined" onClick={() => { setStatusView('former'); resetPage(); }}>Show former employees</Button>}
      />
    );
  } else if (sortedRows.length === 0) {
    tableBody = (
      <TableEmptyRow
        colSpan={columnCount}
        title="No employees match these filters"
        body={`Nothing matches ${activeFilterSummary}. Clear filters to see all ${formatCount(baseRows.length, viewNoun)}.`}
        action={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
      />
    );
  } else {
    tableBody = pageRows.map((row) => {
      const { employee } = row;
      return (
        <TableRow
          key={employee._id}
          hover
          tabIndex={0}
          aria-label={`View ${employee.name}`}
          onClick={() => handleViewEmployee(employee)}
          onKeyDown={(event) => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              handleViewEmployee(employee);
            }
          }}
          sx={{
            cursor: 'pointer',
            [`&:hover > .${STICKY_CLASS}`]: { bgcolor: sbg.bg.hover },
            '& .employees-row-reveal': { opacity: 0, transition: transition('opacity') },
            '&:hover .employees-row-reveal, &:focus-within .employees-row-reveal': { opacity: 1 },
            '@media (hover: none)': { '& .employees-row-reveal': { opacity: 1 } },
          }}
        >
          {columns.map((column) => (
            <TableCell
              key={column.key}
              align={column.align}
              className={column.sticky ? STICKY_CLASS : undefined}
              sx={{ minWidth: column.minWidth, maxWidth: column.sticky ? 300 : 240, ...(column.sticky ? stickyBodySx : null) }}
            >
              {column.render(row)}
            </TableCell>
          ))}
          <TableCell align="right" onClick={(event) => event.stopPropagation()} sx={{ width: 88, whiteSpace: 'nowrap' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={`Edit ${employee.name}`}>
                <IconButton
                  size="small"
                  className="employees-row-reveal"
                  aria-label={`Edit ${employee.name}`}
                  onClick={(event) => { event.stopPropagation(); handleEditEmployee(employee); }}
                >
                  <Pencil size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  aria-label={`Actions for ${employee.name}`}
                  aria-haspopup="menu"
                  aria-expanded={selectedEmployee?._id === employee._id ? true : undefined}
                  onClick={(event) => handleMenuOpen(event, employee)}
                >
                  <Ellipsis size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  }

  const showFooter = !isLoading && !error && sortedRows.length > 0;

  const toolbar = (
    <Toolbar
      end={
        <Typography variant="body2" component="p" role="status" aria-live="polite" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
          {countLabel}
        </Typography>
      }
    >
      <SegmentedControl<StatusView>
        size="small"
        aria-label="Employment status"
        value={statusView}
        onChange={(next) => { setStatusView(next); resetPage(); }}
        options={[
          {
            value: 'active',
            label: (
              <>
                Active
                <Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
                  {isLoading ? '' : formatNumber(activeRows.length)}
                </Box>
              </>
            ),
          },
          {
            value: 'former',
            label: (
              <>
                Former
                <Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
                  {isLoading ? '' : formatNumber(formerRows.length)}
                </Box>
              </>
            ),
          },
        ]}
      />
      <SearchField
        label="Search employees"
        placeholder="Search name, email, role or team"
        value={searchTerm}
        onChange={(value) => { setSearchTerm(value); resetPage(); }}
        width={260}
      />
      <FilterSelect
        label="Department"
        allLabel="All departments"
        value={departmentFilter}
        options={departmentOptions}
        onChange={(value) => { setDepartmentFilter(value); resetPage(); }}
        disabled={isLoading}
      />
      <FilterSelect
        label="Role"
        allLabel="All roles"
        value={roleFilter}
        options={roleOptions}
        onChange={(value) => { setRoleFilter(value); resetPage(); }}
        disabled={isLoading}
      />
      {statusView === 'active' && (
        <FilterSelect
          label="Pace"
          allLabel="Any pace"
          value={paceFilter}
          options={paceOptions}
          onChange={(value) => { setPaceFilter(value as PaceFilter); resetPage(); }}
          disabled={!usageReady}
        />
      )}
      {hasFilters && (
        <Button variant="text" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </Toolbar>
  );

  const footer = showFooter ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        columnGap: 2,
        borderTop: `1px solid ${sbg.border.subtle}`,
        pl: 2,
      }}
    >
      <Box sx={{ py: 1.25 }}>
        <PaceLegend showPace={statusView === 'active'} />
      </Box>
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={sortedRows.length}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          resetPage();
        }}
        labelRowsPerPage="Rows per page"
        labelDisplayedRows={({ from, to, count }) => `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`}
        sx={{ borderTop: 0, ml: 'auto' }}
      />
    </Box>
  ) : undefined;

  const kpiLoading = isLoading;
  const paceKpiLoading = isLoading || isLoadingUsage;
  const paceKpiValue = (status: PaceStatus) => (error || hasUsageError ? null : paceCounts[status]);
  // The explanation lives in the caption and the button's name, not an ⓘ
  // icon: a focusable icon inside the toggle would nest interactive controls.
  const paceTile = (status: PaceStatus, caption: string, description: string) => {
    const selected = paceFilter === status && statusView === 'active';
    const count = paceKpiValue(status);
    const interactive = usageReady && !error;
    const name = count === null
      ? `${PACE_META[status].label}: unavailable`
      : `${PACE_META[status].label}: ${formatCount(count, 'employee')}. ${description} Shows only these employees in the table.`;
    // A filter toggle: `selected` sets aria-pressed and the selected look.
    return (
      <KpiTile
        size="md"
        label={PACE_META[status].label}
        value={<KpiCount value={count} unit="employees" unitSingular="employee" />}
        caption={selected ? 'Showing these employees · select again to clear' : caption}
        loading={paceKpiLoading}
        selected={selected}
        onClick={interactive ? () => handlePaceShortcut(status) : undefined}
        aria-label={name}
      />
    );
  };

  return (
    <Box>
      <PageHeader
        overline="Workspace"
        title="Employees"
        meta={[
          departmentLabel,
          `Time off for ${currentYear}`,
          fulfilledTimeStamp ? `Updated ${formatTime(fulfilledTimeStamp)}` : null,
        ]}
        actions={
          <>
            {/* Not `disabled` while syncing, so the button keeps focus and its
                explanation; the handler ignores repeat clicks instead. */}
            <Tooltip describeChild title="Match employees and departments to their manager and sign-in accounts">
              <Button
                variant="text"
                onClick={handleSyncManagers}
                aria-disabled={isSyncing || undefined}
                aria-busy={isSyncing || undefined}
                startIcon={isSyncing ? <CircularProgress size={16} thickness={5} aria-hidden="true" /> : <RefreshCw size={16} />}
              >
                {isSyncing ? 'Syncing…' : 'Sync manager links'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={isExporting}
              aria-busy={isExporting || undefined}
              startIcon={isExporting ? <CircularProgress size={16} thickness={5} aria-hidden="true" /> : <Download size={16} />}
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={handleAddEmployee}>
              Add employee
            </Button>
          </>
        }
      />

      {syncError && (
        <Alert severity="error" onClose={() => setSyncError(null)} sx={{ mb: 3 }}>
          {syncError}
        </Alert>
      )}

      <KpiGrid phoneColumns={2}>
        <KpiTile
          size="md"
          label="Headcount"
          value={<KpiCount value={error ? null : scopedActiveRows.length} unit="active" unitSingular="active" />}
          caption={
            error
              ? 'Employees unavailable'
              : scopedFormerCount > 0
                ? `${formatNumber(scopedFormerCount)} former on file`
                : 'No former employees on file'
          }
          loading={kpiLoading}
        />
        {paceTile(
          'over-allotment',
          `Approved PTO or sick above allotment · ${currentYear}`,
          `Active employees whose approved ${currentYear} PTO or sick hours, taken plus scheduled, exceed their allotment.`
        )}
        {paceTile(
          'over-pace',
          'Projected past allotment by Dec 31',
          `Active employees whose usage so far projects past their ${currentYear} allotment at the current rate.`
        )}
        {paceTile(
          'pending-risk',
          'Pending requests would pass allotment',
          'Active employees who would pass their allotment if their pending requests are approved.'
        )}
      </KpiGrid>

      {hasUsageError && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button variant="text" size="small" startIcon={<RefreshCw size={14} />} onClick={() => { refetchUsage(); }}>
              Try again
            </Button>
          }
        >
          Couldn’t load {currentYear} time-off usage, so the PTO, sick and pace columns show {MISSING}. The directory itself is current.
        </Alert>
      )}

      <TableCard aria-label="Employee directory" toolbar={toolbar} footer={footer}>
        <Table aria-label="Employees" sx={{ minWidth: statusView === 'former' ? 1120 : 1100 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const active = sort.key === column.sortKey;
                return (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={active ? sort.direction : false}
                    sx={{ minWidth: column.minWidth, ...(column.sticky ? stickyHeadSx : null) }}
                  >
                    {column.sortKey ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.direction : NUMERIC_SORTS.includes(column.sortKey) ? 'desc' : 'asc'}
                        onClick={() => handleSort(column.sortKey as SortKey)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
              <TableCell align="right" sx={{ width: 88 }}>
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>
      </TableCard>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => selectedEmployee && handleViewEmployee(selectedEmployee)}>
          <ListItemIcon><Eye size={16} /></ListItemIcon>
          View details
        </MenuItem>
        <MenuItem onClick={() => selectedEmployee && handleViewEmployee(selectedEmployee)}>
          <ListItemIcon><Mail size={16} /></ListItemIcon>
          Generate email
        </MenuItem>
        <MenuItem onClick={() => selectedEmployee && handleEditEmployee(selectedEmployee)}>
          <ListItemIcon><Pencil size={16} /></ListItemIcon>
          Edit employee
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Employees;
